import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const HERO_IMAGE = "/hero/hero.jpg";

export default function Hero() {
  const eyebrowRef = useReveal();
  const headlineRef = useReveal();
  const subRef = useReveal();
  const ctaRef = useReveal();
  const imgRef = useReveal();
  const metaRef = useReveal();

  return (
    <section
      data-testid="hero-section"
      className="relative pt-20 md:pt-24 lg:pt-28 pb-10 md:pb-12 lg:pb-14 overflow-hidden"
    >
      {/* Decorative editorial markers */}
      <div className="hidden lg:block absolute top-28 left-0 w-px h-24 bg-borderwarm" aria-hidden />
      <div className="hidden lg:block absolute top-28 left-0 w-24 h-px bg-borderwarm" aria-hidden />

      <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left — copy column */}
        <div className="lg:col-span-7">
          <div ref={eyebrowRef} className="reveal flex items-center gap-3 mb-5 lg:mb-6">
            <span className="h-px w-8 bg-oxblood" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-oxblood font-medium">
              Auraa · Hospitality Atelier
            </span>
          </div>

          <h1
            ref={headlineRef}
            className="reveal reveal-delay-1 font-serif text-[36px] leading-[1.05] sm:text-[42px] md:text-[52px] lg:text-[54px] xl:text-[62px] tracking-[-0.02em] text-espresso text-balance"
          >
            Luxury Hospitality{" "}
            <span className="italic font-normal text-oxblood">Uniform Design</span>{" "}
            <span className="text-espresso/85">& Rollout Across India</span>
          </h1>

          <p
            ref={subRef}
            data-testid="hero-subheadline"
            className="reveal reveal-delay-2 mt-5 lg:mt-6 max-w-xl text-[15px] md:text-[16px] lg:text-[17px] leading-[1.65] text-espresso-soft font-light"
          >
            Custom hospitality uniform systems for hotels, boutique
            properties, resorts and premium dining brands — designed around
            your guest experience and executed nationally.
          </p>

          <div
            ref={ctaRef}
            className="reveal reveal-delay-3 mt-7 lg:mt-8 flex flex-wrap items-center gap-4 sm:gap-5"
          >
            <a
              href="https://calendly.com/archanaura8/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="btn-primary group !py-[14px] !px-7"
            >
              Book Design Consultation
              <ArrowUpRight
                size={15}
                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
            <Link
              to="/consultation"
              data-testid="hero-secondary-cta"
              className="btn-secondary !py-[14px] !px-7"
            >
              Request Callback
            </Link>
            <Link
              to="/projects"
              data-testid="hero-tertiary-cta"
              className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-medium text-espresso hover:text-oxblood transition-colors duration-500 whitespace-nowrap"
            >
              <span className="border-b border-espresso/40 group-hover:border-oxblood pb-1 transition-colors duration-500">
                View Selected Projects
              </span>
              <ArrowRight
                size={13}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          </div>

          {/* Compact meta row */}
          <div
            ref={metaRef}
            className="reveal reveal-delay-4 mt-7 lg:mt-9 pt-5 border-t border-borderwarm flex flex-wrap items-baseline gap-x-8 gap-y-2"
          >
            <span className="text-[10px] uppercase tracking-[0.32em] text-espresso-soft">
              Atelier · Est. India
            </span>
            <span className="font-serif italic text-[16px] md:text-[17px] text-espresso">
              Concept · Cut · Couture
            </span>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.32em] text-espresso-soft ml-auto">
              Pan-India Rollout
            </span>
          </div>
        </div>

        {/* Right — image */}
        <div ref={imgRef} className="reveal reveal-delay-2 lg:col-span-5 relative">
          <div className="relative aspect-[4/5] lg:aspect-[4/5] xl:aspect-[3/4] overflow-hidden bg-sandstone">
            <img
              src={HERO_IMAGE}
              alt="The Auraa atelier — tailor at work on a hospitality uniform"
              loading="eager"
              className="w-full h-full object-cover animate-slow-zoom"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/35 via-espresso/5 to-transparent pointer-events-none" />
            <div className="absolute left-5 top-5 text-bone/95 text-[9px] uppercase tracking-[0.32em]">
              No. 01 — Atelier
            </div>

            {/* Compact in-image caption */}
            <div className="absolute left-5 right-5 md:left-6 md:right-6 bottom-5 md:bottom-6">
              <div className="inline-block bg-bone/95 backdrop-blur-sm border border-borderwarm px-4 py-3 max-w-[280px]">
                <div className="text-[9px] uppercase tracking-[0.32em] text-oxblood mb-1">
                  Programme
                </div>
                <div className="font-serif text-[13px] md:text-[14px] leading-snug text-espresso">
                  Concept → Sampling → National Rollout → Replenishment
                </div>
              </div>
            </div>
          </div>

          {/* Architectural border */}
          <div className="hidden md:block absolute -bottom-5 -right-5 w-20 h-20 border border-borderwarm -z-10" />
        </div>
      </div>
    </section>
  );
}
